/**
 * Renders a bar chart
 */

import React from 'react';

const BarChart = class BarChart extends React.Component {
  constructor() {
    super();

    // Set the initial state
    this.state = {
      selected: null,
      width: 0
    };

    // Create refs
    this.chart = React.createRef();
  }

  componentDidMount() {
    setTimeout(() => {
      this.getDimensions();
    }, 100);

    window.addEventListener('resize', e => {
      this.resizeTimer = setTimeout(() => {
        this.getDimensions();
      }, 100);
    });
  }

  getDimensions() {
    if (this.chart.current) {
      this.setState({
        width: this.chart.current.getBoundingClientRect().width
      });
    }
  }

  fill(value) {
    const { max } = this.props;
    const pct = (value / max) * 100;

    if (pct > 75) {
      return 'blue';
    } else if (pct > 50) {
      return 'green';
    } else if (pct > 25) {
      return 'yellow';
    }

    return 'red';
  }

  getBarHeight(value, max) {
    const trueHeight = (value / max) * 100;

    return trueHeight || 1;
  }

  renderStructuredData = (max, levelArr) => {
    const { data, height = 250, scoreColors, units } = this.props;
    const structuredLevels = levelArr.map((level, i) => {
      const range = max / levelArr.length;
      return {
        level,
        min: Math.floor(i * range),
        max: Math.floor(i * range + range)
      };
    });

    const rows = data.map((column, i) => (
      <tr className="barchart__col" key={i} style={{ height: height }}>
        <th id={`barchart-col-${i}`} className="barchart__x-lbl" scope="row">
          {column.label}
        </th>
        {structuredLevels.map((level, j) => {
          // This structured data model only works for bar graphs with a single
          // bar per X-Axis label. @TODO Make this work for more than 1 value per colum.
          let value = column.value[0];
          let intVal = value ? parseInt(value) : 0;

          let show =
            (intVal >= level.min && intVal < level.max) ||
            (intVal === max && j === structuredLevels.length - 1);

          return (
            <td
              className={`barchart__bar${
                scoreColors ? ` barchart__bar--${this.fill(value)}` : ''
              }${column.className ? ` ${column.className}` : ''}${show ? '' : ' meta'}`}
              key={j}
              {...(show && {
                height: `${this.getBarHeight(intVal, max)}%`,
                title: column.label
              })}
            >
              {show && <span className="meta">{`${intVal} ${units || ''}`}</span>}
            </td>
          );
        })}
      </tr>
    ));

    return <tbody>{rows}</tbody>;
  };

  render() {
    const {
      data,
      emptyText,
      height = 250,
      hideScores,
      keyLabels,
      scoreColors,
      title,
      units,
      yLabels
    } = this.props;
    let { levels, max } = this.props;
    let levelArr = [];

    if (!max) {
      data.forEach(column => {
        if (column.value > max) {
          max = column.value;
        }
      });
    }

    if (levels && !yLabels) {
      const increment = max / levels;

      for (let i = 0; i < levels; i++) {
        if (i !== 0) {
          levelArr.push(Math.floor(increment * i));
        }
      }
    } else if (yLabels) {
      levelArr = yLabels;
    }

    return (
      <div className="chart">
        <table className="barchart" style={{ height: height }}>
          {title && <caption>{title}</caption>}

          {/**
           * X-Axis only
           */}
          {max !== 0 && !yLabels && (
            <React.Fragment>
              <thead>
                <tr className="barchart__y-axis">
                  <th>0{units || ''}</th>
                  {levelArr.map((level, i) => (
                    <th key={i}>
                      {level}
                      {!yLabels && (units || '')}
                    </th>
                  ))}
                  <th>
                    {max} {units || ''}
                  </th>
                </tr>
              </thead>

              <tbody>
                {data.map((column, i) => (
                  <tr className="barchart__col" key={i} style={{ height: height }}>
                    <th id={`barchart-col-${i}`} className="barchart__x-lbl" scope="row">
                      {column.label}
                    </th>

                    {column.value.map((value, j) => (
                      <td
                        className={`barchart__bar${
                          scoreColors ? ` barchart__bar--${this.fill(value)}` : ''
                        }${column.className ? ` ${column.className}` : ''}`}
                        key={j}
                        style={{ height: `${this.getBarHeight(value, max)}%` }}
                        title={column.label}
                        aria-labelledby={`barchart-col-${i}`}
                      >
                        <span
                          className={`barchart__bar-lbl chart__tooltip${
                            hideScores ? ' meta' : ''
                          }`}
                        >
                          {value}
                          {units || ''}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </React.Fragment>
          )}

          {/**
           * Render with X-Axis & Y-Axis labels
           */}
          {max !== 0 && yLabels && (
            <React.Fragment>
              <thead>
                <tr className="barchart__y-axis">
                  <td> </td>
                  {levelArr.map((level, i) => (
                    <th scope="col" key={i}>
                      {level}
                      {!yLabels && (units || '')}
                    </th>
                  ))}
                </tr>
              </thead>

              {this.renderStructuredData(max, levelArr)}
            </React.Fragment>
          )}
        </table>

        {keyLabels && keyLabels.length > 0 && (
          <ul className="chart__key chart__key--bar">
            {keyLabels.map((label, i) => (
              <li key={i}>{label}</li>
            ))}
          </ul>
        )}

        {max === 0 && <span className="chart__no-results">{emptyText}</span>}
      </div>
    );
  }
};

export default BarChart;
